import NeptuneElement from "../../NeptuneElement.js";

class TabGroup extends NeptuneElement {
    
    templateMarkup = `
        <div role="tablist" aria-label="Tabs">
            <div>
                <slot name="tab"></slot>
            </div>
        </div>
        <div>
            <slot name="tabPanel"></slot>
        </div>
    `;

    componentCssUrl = '/src/components/container/tabgroup/tab-group.css';

    constructor() {
        super();
    }

    static get observedAttributes() {
        return ['orientation'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'orientation' && this.isRendered) {
            this.processOrientation();
        }
    }

    async connectedCallback() {
        await super.connectedCallback();
        this.handleSlotChange = this.handleSlotChange.bind(this);
        this.handlePanelKeyDown = this.handlePanelKeyDown.bind(this);
        this.setEventListeners();
        this.initializeTabs();
    }

    async disconnectedCallback() {
        this.removeEventListeners();
        await super.disconnectedCallback();
    }

    setEventListeners() {
        const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
        const panelSlot = this.shadowRoot.querySelector('slot[name="tabPanel"]');
        
        if (tabSlot) {
            tabSlot.addEventListener('slotchange', this.handleSlotChange);
        }
        if (panelSlot) {
            panelSlot.addEventListener('slotchange', this.handleSlotChange);
        }
    }

    removeEventListeners() {
        const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
        const panelSlot = this.shadowRoot.querySelector('slot[name="tabPanel"]');
        
        if (tabSlot) {
            tabSlot.removeEventListener('slotchange', this.handleSlotChange);
        }
        if (panelSlot) {
            panelSlot.removeEventListener('slotchange', this.handleSlotChange);
        }

        const panels = panelSlot?.assignedElements() || [];
        panels.forEach(panel => {
            const panelDiv = panel.shadowRoot?.querySelector('div[role="tabpanel"]');
            if (panelDiv) {
                panelDiv.removeEventListener('keydown', this.handlePanelKeyDown);
            }
        });
    }

    handleSlotChange() {
        this.initializeTabs();
    }

    handlePanelKeyDown(e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
            const tabs = tabSlot?.assignedElements() || [];
            const activeTab = tabs.find(tab => tab.hasAttribute('active'));
            if (activeTab) {
                activeTab.focus();
            }
        }
    }

    initializeTabs() {
        const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
        const panelSlot = this.shadowRoot.querySelector('slot[name="tabPanel"]');
        
        if (!tabSlot || !panelSlot) return;

        const tabs = tabSlot.assignedElements();
        const panels = panelSlot.assignedElements();

        tabs.forEach((tab, index) => {
            const tabId = tab.getAttribute('id') || `tab-${Math.random().toString(36).substr(2, 9)}`;
            const panelId = `panel-${tabId}`;
            
            tab.setAttribute('id', tabId);
            
            const tabButton = tab.shadowRoot?.querySelector('button');
            if (tabButton) {
                tabButton.setAttribute('aria-controls', panelId);
            }

            if (panels[index]) {
                const panelDiv = panels[index].shadowRoot?.querySelector('div[role="tabpanel"]');
                if (panelDiv) {
                    panelDiv.setAttribute('id', panelId);
                    panelDiv.setAttribute('aria-labelledby', tabId);
                    panelDiv.setAttribute('tabindex', '-1');
                    
                    panelDiv.removeEventListener('keydown', this.handlePanelKeyDown);
                    panelDiv.addEventListener('keydown', this.handlePanelKeyDown);
                }
            }
        });

        this.processOrientation();

        const hasActiveTab = tabs.some(tab => tab.hasAttribute('active'));
        
        if (!hasActiveTab && tabs.length > 0) {
            tabs[0].setAttribute('active', '');
            panels.forEach((panel, index) => {
                const panelDiv = panel.shadowRoot?.querySelector('div[role="tabpanel"]');
                if (panelDiv) {
                    if (index === 0) {
                        panelDiv.removeAttribute('hidden');
                        panelDiv.removeAttribute('aria-hidden');
                    } else {
                        panelDiv.setAttribute('hidden', '');
                        panelDiv.setAttribute('aria-hidden', 'true');
                    }
                }
            });
        } else {
            tabs.forEach((tab, index) => {
                const panelDiv = panels[index]?.shadowRoot?.querySelector('div[role="tabpanel"]');
                if (panelDiv) {
                    if (tab.hasAttribute('active')) {
                        panelDiv.removeAttribute('hidden');
                        panelDiv.removeAttribute('aria-hidden');
                    } else {
                        panelDiv.setAttribute('hidden', '');
                        panelDiv.setAttribute('aria-hidden', 'true');
                    }
                }
            });
        }
    }

    activateTab(tabId) {
        if (!tabId) return;
        
        const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
        const panelSlot = this.shadowRoot.querySelector('slot[name="tabPanel"]');
        
        if (!tabSlot || !panelSlot) return;

        const tabs = tabSlot.assignedElements();
        const panels = panelSlot.assignedElements();

        tabs.forEach((tab, index) => {
            const panelDiv = panels[index]?.shadowRoot?.querySelector('div[role="tabpanel"]');
            
            if (tab.getAttribute('id') === tabId) {
                tab.setAttribute('active', '');
                if (panelDiv) {
                    panelDiv.removeAttribute('hidden');
                    panelDiv.removeAttribute('aria-hidden');
                }
            } else {
                tab.removeAttribute('active');
                if (panelDiv) {
                    panelDiv.setAttribute('hidden', '');
                    panelDiv.setAttribute('aria-hidden', 'true');
                }
            }
        });

        this.dispatchEvent(new CustomEvent('neptune-tab-change', {
            bubbles: true,
            composed: true,
            detail: {
                tabId: tabId,
                tabIndex: tabs.findIndex(tab => tab.getAttribute('id') === tabId)
            }
        }));
    }

    focusNextTab(currentTab, shouldActivate = false) {
        const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
        const tabs = tabSlot?.assignedElements() || [];
        const currentIndex = tabs.indexOf(currentTab);
        const nextIndex = (currentIndex + 1) % tabs.length;
        const nextTab = tabs[nextIndex];
        
        if (shouldActivate) {
            this.activateTab(nextTab.getAttribute('id'));
        }
        nextTab.focus();
    }

    focusPreviousTab(currentTab, shouldActivate = false) {
        const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
        const tabs = tabSlot?.assignedElements() || [];
        const currentIndex = tabs.indexOf(currentTab);
        const prevIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        const prevTab = tabs[prevIndex];
        
        if (shouldActivate) {
            this.activateTab(prevTab.getAttribute('id'));
        }
        prevTab.focus();
    }

    focusFirstTab(shouldActivate = false) {
        const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
        const tabs = tabSlot?.assignedElements() || [];
        if (tabs.length > 0) {
            if (shouldActivate) {
                this.activateTab(tabs[0].getAttribute('id'));
            }
            tabs[0].focus();
        }
    }

    focusLastTab(shouldActivate = false) {
        const tabSlot = this.shadowRoot.querySelector('slot[name="tab"]');
        const tabs = tabSlot?.assignedElements() || [];
        if (tabs.length > 0) {
            const lastTab = tabs[tabs.length - 1];
            if (shouldActivate) {
                this.activateTab(lastTab.getAttribute('id'));
            }
            lastTab.focus();
        }
    }

    processOrientation() {
        const isVertical = this.getAttribute('orientation') === 'vertical';
        const tabSlot = this.shadowRoot?.querySelector('slot[name="tab"]');
        const tabs = tabSlot?.assignedElements() || [];
        tabs.forEach(tab => {
            if (isVertical) {
                tab.setAttribute('orientation', 'vertical');
            } else {
                tab.removeAttribute('orientation');
            }
        });
    }
}

customElements.define('neptune-tab-group', TabGroup);
export default TabGroup;